import CloseIcon from "@mui/icons-material/Close";
import {LoadingButton} from "@mui/lab";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Stack,
    TextField,
    useMediaQuery
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import type {Printout, PrintoutTemplate} from "crqlar-react-common/dist/models/printout/printout.model";
import {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import {PrintoutService} from "crqlar-react-common/dist/services/printout/printout.service";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import InfoTooltip from "../../common/InfoTooltip";
import PrintoutDocumentRenderer from "../print/PrintoutDocumentRenderer";
import {usePrintoutPreviewContext} from "../print/usePrintoutPreviewContext";
import {resolvePrintoutDocument} from "../core/printoutDocument";
import TemplatePicker, {BLANK_TEMPLATE_KEY} from "../../common/pickers/TemplatePicker";

interface PrintoutCreateDialogProps {
    open: boolean;
    onClose: () => void;
}

const DialogField = ({label, hint, children}: {label: string, hint: string, children: React.ReactNode}) => (
    <Stack gap={0.75}>
        <Stack direction={'row'} alignItems={'center'}>
            <Typography variant={'body2'} fontWeight={500}>
                {label}
            </Typography>
            <InfoTooltip title={hint}/>
        </Stack>
        {children}
    </Stack>
);

const PrintoutCreateDialog = ({open, onClose}: PrintoutCreateDialogProps) => {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const readOnly = useReadOnly();

    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const showPreview = useMediaQuery(theme.breakpoints.up('md'));

    const [templates, setTemplates] = useState<PrintoutTemplate[]>([]);
    const [blankDraft, setBlankDraft] = useState<Printout>();
    const [selectedKey, setSelectedKey] = useState(BLANK_TEMPLATE_KEY);
    const [nameIsSuggestion, setNameIsSuggestion] = useState(true);

    const printoutData = useEntity<Printout>({
        get: async () => {
            const [availableTemplates, blank] = await Promise.all([
                PrintoutService.getPrintoutTemplates({language: i18n.language}),
                PrintoutService.getBlankPrintout(),
            ]);
            setTemplates(availableTemplates);
            setBlankDraft(blank);
            return {
                ...blank,
                name: t('New empty printout'),
            };
        },
        /*
            A template is created by the backend from its id, so the printout that lands in the list
            is the one the preview showed - the dashboard never assembles the document itself.
        */
        create: (draft) => {
            const template = templates.find(entry => entry.id === selectedKey);
            return template
                ? PrintoutService.createPrintoutFromTemplate({
                    templateId: template.id,
                    language: i18n.language,
                    name: draft.name,
                })
                : PrintoutService.createPrintout(draft);
        },
        // useEntity requires both handlers even though this dialog only creates.
        update: PrintoutService.updatePrintout,
        isValid: (printout) => !!printout.name?.trim() && !!printout.type,
        cleanupBeforeSave: (printout) => ({...printout, name: printout.name?.trim() || undefined}),
        onSaved: (printout) => {
            onClose();
            navigate(`/system/printouts/${printout.id}/editor`);
        },
    }, "new");

    const printout = printoutData.item;

    const safeClose = () => {
        if (!printoutData.saving) onClose();
    };

    const saveNow = () => {
        if (!printoutData.isValid || readOnly || printoutData.saving) return;
        printoutData.setErrorSaving(false);
        void printoutData.save();
    };

    const nameRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        const timer = window.setTimeout(() => {
            nameRef.current?.focus();
            nameRef.current?.select();
        }, 250);
        return () => window.clearTimeout(timer);
    }, []);

    const nameMissing = !printout.name?.trim();

    /**
     * Swaps the draft for the one the backend built for this template (or the empty starter),
     * preserving an author-written name.
     */
    const applyTemplate = (key: string) => {
        const template = templates.find(entry => entry.id === key);
        const base = template?.draft ?? blankDraft;
        if (!base) return;

        const keepTyped = !!printout.name?.trim() && !nameIsSuggestion;
        const suggestion = template ? template.name : t('New empty printout');

        setSelectedKey(key);
        setNameIsSuggestion(!keepTyped);
        printoutData.setItem({
            ...base,
            name: keepTyped ? printout.name : suggestion,
            // A template carries its own type; going blank keeps whatever the select says.
            type: template ? base.type : printout.type,
        });
    };

    /**
     * The type owns which templates are offered, so a template of another type cannot survive the
     * change — the draft falls back to blank, keeping only a name the author typed themselves.
     */
    const handleTypeChange = (value: string) => {
        const type = value as PrintoutType;

        const selected = templates.find(entry => entry.id === selectedKey);
        if (selected && selected.type !== type && blankDraft) {
            const keepTyped = !!printout.name?.trim() && !nameIsSuggestion;
            setSelectedKey(BLANK_TEMPLATE_KEY);
            setNameIsSuggestion(!keepTyped);
            printoutData.setItem({
                ...blankDraft,
                name: keepTyped ? printout.name : t('New empty printout'),
                type,
            });
            return;
        }

        printoutData.setItem(previous => ({...previous, type}));
    };

    const templateOptions = useMemo(
        () => templates
            .filter(template => !printout.type || template.type === printout.type)
            .map(template => ({
                key: template.id,
                name: template.name,
                description: template.description,
            })),
        [printout.type, templates],
    );

    const form = (
        <Stack gap={3}>
            <Stack gap={2}>
                <DialogField
                    label={`${t('Name')} *`}
                    hint={t('Internal name of this printout, used to find it in lists and print menus.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        inputRef={nameRef}
                        value={printout.name || ''}
                        onChange={(e) => {
                            const name = e.target.value;
                            setNameIsSuggestion(false);
                            printoutData.setItem(previous => ({...previous, name}));
                        }}
                        onFocus={(e) => (e.target as HTMLInputElement).select()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') saveNow();
                        }}
                        fullWidth
                        required
                        disabled={readOnly}
                        error={nameMissing}
                        // Reserve helper-text height to avoid a validation layout shift.
                        helperText={nameMissing ? t('Name is required') : ' '}
                    />
                </DialogField>

                <DialogField
                    label={`${t('Printout type')} *`}
                    hint={t('Determines which print workflow uses this printout and which data it can show. Narrows the templates below.')}
                >
                    <FormControl fullWidth>
                        <Select
                            id={'type'}
                            value={printout.type ?? ''}
                            onChange={(e) => handleTypeChange(e.target.value)}
                            size={'small'}
                            disabled={readOnly}
                        >
                            {!printout.type && (
                                <MenuItem value={''} disabled>{t('(Not set)')}</MenuItem>
                            )}
                            <MenuItem value={PrintoutType.RESERVATION_CONFIRMATION}>{t('Reservation confirmation')}</MenuItem>
                            <MenuItem value={PrintoutType.RESERVATION_CANCELLATION}>{t('Reservation cancellation')}</MenuItem>
                            <MenuItem value={PrintoutType.FOLIO_CONFIRMATION}>{t('Folio confirmation')}</MenuItem>
                            <MenuItem value={PrintoutType.AGENDA_HANDOUT}>{t('Agenda handout')}</MenuItem>
                        </Select>
                    </FormControl>
                </DialogField>
            </Stack>

            <TemplatePicker
                id={'printout_template'}
                options={templateOptions}
                selectedKey={selectedKey}
                onSelect={applyTemplate}
                blank={{key: BLANK_TEMPLATE_KEY, name: t('New empty printout'), description: t('Start from an empty page')}}
                disabled={printoutData.saving || readOnly}
            />
        </Stack>
    );

    const previewDocument = useMemo(() => resolvePrintoutDocument(printout), [printout]);
    const previewContext = usePrintoutPreviewContext(
        printout.type,
        previewDocument?.language,
        printout.scopedSectorIds,
    );

    const formColumn = (
        <Box
            sx={{
                flex: showPreview ? '1 1 45%' : 1,
                minWidth: 0,
                overflowY: 'auto',
                py: 3,
                pr: showPreview ? 1 : 0,
            }}
        >
            {form}
        </Box>
    );

    const previewColumn = (
        <Box sx={{flex: '1 1 55%', minWidth: 0, py: 3, display: 'flex', flexDirection: 'column'}}>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: '#eef2f7',
                    p: 2,
                }}
            >
                {/* Template changes remount the measured preview; blank means one empty page. */}
                <PrintoutDocumentRenderer
                    key={selectedKey}
                    printout={printout}
                    context={previewContext}
                    language={previewDocument?.language}
                />
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={safeClose}
            maxWidth={'lg'}
            fullWidth
            fullScreen={fullScreen}
            PaperProps={{sx: fullScreen ? {} : {height: '85vh', maxHeight: '85vh'}}}
            aria-labelledby={'create-printout-dialog-title'}
        >
            <DialogTitle sx={{pb: 1}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
                    <Typography id={'create-printout-dialog-title'} variant={'h6'} fontWeight={600} noWrap>
                        {t('Create printout')}
                    </Typography>
                    <IconButton onClick={safeClose} size={'small'} aria-label={t('Close')}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{px: 3, py: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                {showPreview
                    ? (
                        <Stack direction={'row'} gap={3} sx={{flex: 1, minHeight: 0, minWidth: 0}}>
                            {formColumn}
                            {previewColumn}
                        </Stack>
                    )
                    : formColumn}
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={safeClose} disabled={printoutData.saving}>
                    {t('Cancel')}
                </Button>
                <LoadingButton
                    id={'btn-entity-save'}
                    variant={'contained'}
                    onClick={saveNow}
                    loading={printoutData.saving}
                    disabled={!printoutData.isValid || readOnly}
                >
                    {t('Create printout')}
                </LoadingButton>

                {printoutData.renderSnackbars()}
            </DialogActions>
        </Dialog>
    );
};

export default PrintoutCreateDialog;
